import * as Sentry from '@sentry/browser';
import * as addressActions from '~/actions/address.actions';
import { ActionKeys } from '~/actions/constants';
import * as ordersActions from '~/actions/orders.actions';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import { fetchShipment, selectRate, clearRates } from '~/actions/shipments.actions';
import { fetchStores } from '~/actions/stores.actions';
import { setIdVerificationStatus } from '~/actions/user.actions';
import axios, { AxiosResponse } from 'axios';
import { push, replace } from 'connected-react-router';
import { RETURN_ADDRESS_KIND, SENDER_ADDRESS_KIND } from '~/constants/addressBook';
import { CANADA_POST, DHL_EXPRESS, UPS } from '~/constants/carriers';
import countries, { CountryIso2 } from '~/constants/countries';
import { PARCEL_FORM_NAME, SPLIT_ORDERS_FORM } from '~/constants/form';
import {
  ADDITIONAL_SERVICES_SAVE,
  ADDITIONAL_SERVICES_SAVE_FAILED,
  ORDER_CHECKOUT_STARTED,
  SPLIT_ORDER_MODAL_CLOSED,
  SPLIT_ORDER_MODAL_OPENED,
  SPLIT_ORDER_MODAL_SAVE,
} from '~/constants/user-events';
import { LocationDescriptorObject } from 'history';
import compact from 'lodash-es/compact';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { PreCheckoutSteps } from '~/reducers/orders/orders.reducer';
import {
  change,
  getFormValues,
  setSubmitFailed,
  setSubmitSucceeded,
  startSubmit,
  stopSubmit,
} from 'redux-form';
import {
  call,
  delay,
  fork,
  ForkEffect,
  put,
  race,
  SagaReturnType,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import * as addressSagas from '~/sagas/address/address.saga';
import {
  findResetBatchEntries,
  getBatchEntriesIdsToUpdate,
  orderSelectionToBatchEntries,
} from '~/sagas/batch-entry-utils';
import { refreshUser } from '~/sagas/user/user.saga';
import {
  getIncompleteRegStatusFromRates,
  getUnpurchasedBatchEntriesOrderSelectionWithExpiredShipmentDate,
} from '~/selectors/batch-entry.composite.selectors';
import {
  getBatchEntriesByIds,
  getBatchEntriesByOrderObjectIds,
  getBatchEntryById,
  getFirstBatchEntryIdFromOrder,
  getIsAnyBatchEntryASplitOrder,
} from '~/selectors/batch-entry.selectors';
import { getCarrierAccountsObjectIdMap } from '~/selectors/carrier-accounts.selectors';
import { getIsOldExpChosen } from '~/selectors/features.selectors';
import {
  getLastOrderId,
  getOrders,
  getOrdersData,
  getOrderSuccessfulTransactionIds,
  getSelectedOrders,
  getTotalOrders,
} from '~/selectors/orders.selectors';
import { getLocation, getPathname, getQS } from '~/selectors/router.selectors';
import { getShipmentIsLoading, getShipmentsData } from '~/selectors/shipments.selectors';
import { hasStoreConnected } from '~/selectors/stores.selectors';
import {
  getIsIDVRequired,
  getIsUserAddressValid,
  getRequiresEmailVerification,
  userHasValidBilling,
  userIsBillingBlocked,
  userIsHighFraudRisk,
} from '~/selectors/user.selectors';
import * as ordersService from '~/services/orders.service';
import { emailNotify } from '~/services/transactions.service';
import { getVerificationStatus } from '~/services/user.service';
import { sanitizeAddress } from '~/types/Address';
import APIAddress from '~/types/APIAddress';
import { FilterGroup, JSONBusinessRule } from '~/types/BusinessRule';
import CarrierAccount from '~/types/CarrierAccount';
import { RegStatus, RegStatusByCarrierAccount } from '~/types/CarrierAccountRegStatus';
import * as IDVerification from '~/types/IDVerification';
import Order, * as order from '~/types/Order';
import {
  BatchEntryExtended,
  batchEntryExtendedFromJSON,
  JSONBatchEntriesResponse,
  JSONBatchEntryExtended,
  partialFulfillmentItemContentToJSON,
  ShipmentExtras,
} from '~/types/OrderBatchEntry';
import * as ordersBatch from '~/types/OrdersBatch';
import { JSONOrdersBatch } from '~/types/OrdersBatch';
import * as parcel from '~/types/Parcel';
import Shipment, { ShipmentStatus } from '~/types/Shipment';
import {
  Transaction,
  TransactionStatus,
  JSONTransactionUI2,
  transactionUI2FromJSON,
} from '~/types/Transaction';
import { parseBusinessRules } from '~/utils/businessRules';
import { batchAction } from '~/utils/redux/batch';
import { Events } from '~/utils/segment';
import {
  requestUpdatedTrack,
  trackDownload,
  trackWithGlobalProps,
} from '~/utils/TrackingHelper/utils';
import { unknownToString } from '~/utils/utils';
import { selectState } from '..';
import { RATES_UPDATED_EVENT } from '~/utils/TrackingHelper/types';
import { trackingHelper } from '~/utils/TrackingHelper';
import { getTransactionsData } from '~/selectors/transactions.selectors';
import { addSnackbarMessage } from '~/actions/snackbar.actions';
import {
  getOrders as getWebOrders,
  type GetOrdersResponseBody,
} from '~/services/web-orders.service';

function transformFilters(filters: FilterGroup, orderStatus: string) {
  if (!filters) return {};

  return {
    ...filters,
    order_status:
      orderStatus === 'pending'
        ? [
            order.OrderStatus.PAID,
            order.OrderStatus.UNKNOWN,
            order.OrderStatus.AWAITPAY,
            order.OrderStatus.PARTIALLY_FULFILLED,
          ]
        : orderStatus === 'fulfilled'
          ? [order.OrderStatus.SHIPPED]
          : orderStatus === 'cancelled'
            ? [order.OrderStatus.CANCELLED]
            : [],
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isOrderSearch(filters: any) {
  return filters['order_status'].length > 0 || Object.keys(filters).length > 1;
}

export function* fetchOrder(action: ReturnType<typeof ordersActions.fetchOrder>) {
  try {
    const orderId = action.orderId;
    const isOldExpChosen: boolean = yield selectState<boolean>(getIsOldExpChosen);
    const service = isOldExpChosen ? ordersService.getLegacy : ordersService.get;
    const response: AxiosResponse<order.JSONOrder> = yield call(service, orderId);
    const payload: ordersActions.FetchOrderPayload = {
      order: order.fromJSON(response.data),
    };
    yield put(ordersActions.fetchOrderSuccess(payload));
  } catch (err) {
    yield put(ordersActions.fetchOrderError());
  }
}

export const getOrderIdsByTags = async (tags: string[]) => {
  const webOrdersByTags: AxiosResponse<GetOrdersResponseBody> = await getWebOrders({
    tag: tags,
  });

  return webOrdersByTags.data.results.map((order) => order.object_id);
};

export function* fetchOrders(action: ReturnType<typeof ordersActions.fetchOrders>) {
  try {
    const isOldExpChosen: boolean = yield selectState<boolean>(getIsOldExpChosen);
    const { tag, ...filters } = action.payload.filters;
    const transformedFilters = transformFilters(filters, action.payload.orderStatus);

    const tagsOrderIds: string[] | undefined =
      tag?.length > 0 ? yield call(getOrderIdsByTags, tag) : undefined;

    const response: AxiosResponse<{
      results: order.JSONOrder[];
      count: number;
    }> = yield isOldExpChosen
      ? call(
          ordersService.getAllLegacy,
          action.payload.page,
          transformedFilters,
          action.payload.fetchCount,
          action.payload.showOnlyHidden,
          action.payload.sort,
        )
      : call(
          ordersService.getAll,
          action.payload.page,
          transformedFilters,
          action.payload.fetchCount,
          action.payload.showOnlyHidden,
          action.payload.sort,
          action.payload.sortBy,
          action.payload.orderBy,
          tagsOrderIds,
        );
    const parsedOrders = response.data.results.map(order.fromJSON);
    const totalOrderCount = response.data.count;

    if (isOrderSearch(transformedFilters)) {
      yield call(trackWithGlobalProps, action.payload.appLocation, Events.order_search_completed, {
        orderCount: totalOrderCount,
        filters: transformedFilters,
      });
    }

    yield put(ordersActions.fetchOrdersSuccess(parsedOrders, response.data.count));
  } catch (err) {
    Sentry.captureException(err);
    yield put(ordersActions.fetchOrdersError());
  }
}

export function* fetchOrderTags() {
  try {
    yield put(ordersActions.fetchOrderTags());

    const ordersData = yield selectState(getOrders);
    const orderIds = ordersData.map(({ objectId }) => objectId);

    if (orderIds.length === 0) {
      yield put(ordersActions.fetchOrderTagsError());

      return;
    }

    const response: AxiosResponse<{ results: order.WebOrder[] }> = yield call(getWebOrders, {
      id: orderIds,
    });
    const ordersWithTags: order.OrderTagsMapping = response.data?.results?.reduce(
      (orderTags, order) => ({ ...orderTags, [order.object_id]: order.tags || [] }),
      {},
    );

    if (!ordersWithTags || Object.keys(ordersWithTags).length === 0) {
      throw new Error('No tags found for orders');
    }

    yield put(ordersActions.updateOrderTags(ordersWithTags));
    yield put(ordersActions.fetchOrderTagsSuccess());
  } catch (error) {
    // @TODO enable sentry when we fix WEB-713
    // Sentry.captureMessage(`fetch order tags failed - ${error}`, {
    //   level: 'error' as Sentry.SeverityLevel,
    // });
    yield put(ordersActions.fetchOrderTagsError());
  }
}

export function* fetchOrderTransactions(
  action: ReturnType<typeof ordersActions.fetchOrderTransactions>,
) {
  try {
    const result: AxiosResponse<{ results: JSONTransactionUI2[] }> = yield call(
      ordersService.getOrderTransactions,
      action.payload.orderObjectId,
    );
    const parsedTransactions = result.data.results.map(transactionUI2FromJSON);
    yield put(
      ordersActions.fetchOrderTransactionsSuccess(action.payload.orderObjectId, parsedTransactions),
    );
  } catch (err) {
    yield put(ordersActions.fetchOrderTransactionsError());
  }
}

export function* countOrders() {
  try {
    const isOldExpChosen: boolean = yield selectState<boolean>(getIsOldExpChosen);
    const countOrders = isOldExpChosen
      ? ordersService.countOrdersLegacy
      : ordersService.countOrders;
    const response: AxiosResponse<{ results: order.JSONOrder[]; count: number }> =
      yield call(countOrders);
    const lastOrderId: string = yield selectState<string>(getLastOrderId);
    const ordersCount = response.data.count;

    if (ordersCount > 0) {
      const newLastOrderId = order.fromJSON(response.data.results[0]).objectId;

      if (!lastOrderId) {
        yield put(ordersActions.saveLastOrderId(newLastOrderId));
      } else if (lastOrderId !== newLastOrderId) {
        yield put(ordersActions.newOrdersFound(newLastOrderId));
      }
    }

    yield put(ordersActions.countOrderSuccess(ordersCount));
  } catch {
    yield put(ordersActions.countOrderError());
  }
}

export function* syncOrders(action: ReturnType<typeof ordersActions.syncOrders>) {
  const userFirstStoreConnected: boolean = yield selectState<boolean>(hasStoreConnected);
  const totalOrdersInStore: number = yield selectState<number>(getTotalOrders);
  try {
    yield call(ordersService.syncOrders);
    yield call(countOrders);
    yield put(ordersActions.syncOrdersSuccess());
    if (userFirstStoreConnected && totalOrdersInStore === 0) {
      yield put(
        ordersActions.fetchOrders(
          action.payload.appLocation,
          1,
          {},
          '25',
          false,
          '',
          '',
          '_score',
          'desc',
        ),
      );
    }
  } catch (err) {
    yield put(ordersActions.syncOrdersError());
  }
}

export function* ordersPollWatcher() {
  while (true) {
    yield take(ActionKeys.ORDERS_POLL_START);

    yield race({
      task: call(ordersPollWorker),
      cancel: take(ActionKeys.ORDERS_NEW_ORDERS_FOUND),
    });
  }
}

export function* ordersPollWorker() {
  while (true) {
    const POLL_WAIT_TIME = 1000 * 60 * 5; // 5min
    yield call(countOrders);
    yield delay(POLL_WAIT_TIME);
  }
}

export function* firstTimeSyncWatcher() {
  while (true) {
    yield take(ActionKeys.ORDERS_FIRST_TIME_SYNC_POLL_START);

    yield race({
      task: call(firstTimeSyncWorker),
      cancel: take(ActionKeys.ORDERS_FIRST_TIME_SYNC_POLL_END),
    });
  }
}

export function* firstTimeSyncWorker() {
  while (true) {
    const POLL_WAIT_TIME = 1000 * 2; // 2 sec

    const response: AxiosResponse<{
      results: order.JSONOrder[];
    }> = yield call(ordersService.countOrders);

    if (response.data.results.length > 0) {
      yield put(ordersActions.fetchOrders('other', 1, {}, '', false, '', '', 'relevance', 'desc'));
      yield put(fetchStores());
      yield put(ordersActions.stopFirstTimeSyncOrdersPoll());
    }

    yield delay(POLL_WAIT_TIME);
  }
}

export function* getAllLabels(action: ReturnType<typeof ordersActions.getAllLabels>) {
  try {
    const orderObjectIds = Object.keys(action.payload.orderSelection);
    const batchEntryIds = Object.values(action.payload.orderSelection).reduce(
      (l, v) => [...l, ...v],
      [],
    );
    const response: AxiosResponse<{ url: string }> = yield call(
      ordersService.getAllLabels,
      orderObjectIds,
      batchEntryIds,
    );
    yield put(ordersActions.getAllLabelsSuccess(response.data.url));
  } catch (err) {
    if (axios.isAxiosError(err) && err.response && err.response.data) {
      yield put(ordersActions.getAllLabelsError(err.response.data));
    } else {
      yield put(ordersActions.getAllLabelsError('We hit an unexpected error. Please try again.'));
    }
  }
}

export function* getLabel(action: ReturnType<typeof ordersActions.getLabel>) {
  try {
    const response: AxiosResponse<{ url: string }> = yield call(
      ordersService.getLabel,
      action.payload.transactionIds,
      action.payload.fileFormat,
    );
    action.payload.windowRef.location.assign(response.data.url);
    yield put(ordersActions.getLabelSuccess());
  } catch (err) {
    yield put(ordersActions.getLabelError());
  }
}

export function* getOrderPackingSlip(action: ReturnType<typeof ordersActions.getPackingSlips>) {
  try {
    const { orderObjectIds, batchEntryIds } = order.actionableOrderSelectionIds(
      action.payload.orderSelection,
    );

    // TODO: Adding a type for this line upsets the compiler in the tests in a way that I can't explain.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: AxiosResponse<{ slip_url: string }> = yield call(
      ordersService.getOrderPackingSlip,
      orderObjectIds,
      batchEntryIds,
    );
    action.payload.windowRef.location.assign(response.data.slip_url);
    yield call(
      trackDownload,
      action.payload.appLocation,
      'packing_slip',
      action.payload.orderSelection,
    );
  } catch (err) {
    yield put(ordersActions.getOrderPackingSlipError());
  }
}

export function* sendEmailNotification(
  action: ReturnType<typeof ordersActions.sendOrderEmailNotification>,
) {
  const transactionsToEmail: string[] = yield select(getOrderSuccessfulTransactionIds, {
    orderObjectIds: action.payload.ids,
  });

  if (!transactionsToEmail) {
    yield put(ordersActions.sendOrderEmailNotificationError());
    return;
  }

  try {
    yield call(emailNotify, transactionsToEmail);
    yield put(ordersActions.sendOrderEmailNotificationSuccess());
  } catch (err) {
    yield put(ordersActions.sendOrderEmailNotificationError());
  }
}

// Given input of order IDs and batch entry IDs, retrieve transaction IDs
// filtered by the given batch entry IDs (for split order support)
export function* checkoutSendEmailNotification(
  action: ReturnType<typeof ordersActions.checkoutSendEmailNotifications>,
) {
  // support sending emails for batch entries. batch entry has transaction ID
  const transactionsToEmail: string[] = yield select(getOrderSuccessfulTransactionIds, {
    orderObjectIds: action.payload.orderObjectIds,
  });

  if (!transactionsToEmail) {
    yield put(
      ordersActions.checkoutSendEmailNotificationsError(
        'We hit an unexpected problem, please try again.',
      ),
    );
    return;
  }

  const batchEntries: BatchEntryExtended[] = yield select(getBatchEntriesByIds, {
    batchEntryIds: action.payload.batchEntryIds,
  });
  const batchEntryTransactionIds = batchEntries.map((batchEntry) => batchEntry.transactionObjectId);
  const batchEntryTransactions = transactionsToEmail.filter((transactionId) =>
    batchEntryTransactionIds.includes(transactionId),
  );

  try {
    yield call(emailNotify, batchEntryTransactions);
    yield put(ordersActions.checkoutSendEmailNotificationsSuccess());
  } catch (err) {
    // TODO: Proper error
    yield put(
      ordersActions.checkoutSendEmailNotificationsError(
        'We hit an unexpected problem, please try again.',
      ),
    );
  }
}

export function* createBatch(action: ReturnType<typeof ordersActions.createBatch>) {
  try {
    const batchEntries: number[] = [];

    const isOldExpChosen: boolean = yield selectState<boolean>(getIsOldExpChosen);

    // Get batch entries related to orders
    if (!isOldExpChosen) {
      for (const orderObjectId of action.payload.ids) {
        const batchEntry: Maybe<number> = yield select(getFirstBatchEntryIdFromOrder, {
          orderObjectId,
        });
        if (batchEntry) {
          batchEntries.push(batchEntry);
        }
      }
    }

    const response: AxiosResponse<JSONOrdersBatch> = yield call(
      ordersService.createBatch,
      action.payload.ids,
      batchEntries.filter((batchEntry) => batchEntry),
    );
    yield put(ordersActions.createBatchSuccess(ordersBatch.fromJSON(response.data)));

    if (isOldExpChosen) {
      yield put(push({ pathname: `/orders/batch/${response.data.id}` }));
    }
  } catch (err) {
    yield put(ordersActions.createBatchError());
  }
}

export function* hideOrder(action: ReturnType<typeof ordersActions.hideOrders>) {
  try {
    yield call(ordersService.hideOrder, action.payload.ids);
    yield put(ordersActions.hideOrderSuccess());
  } catch (err) {
    yield put(ordersActions.hideOrderError('We hit an unexpected error. Please try again.'));
  }
}

export function* patchBatchEntries(action: ReturnType<typeof ordersActions.patchBatchEntries>) {
  try {
    const { batchEntriesIdsToUpdate } = yield call(
      getBatchEntriesIdsToUpdate,
      action.payload.orderSelection,
    );

    if (!action.payload.parcel && !action.payload.extras) {
      yield put(
        ordersActions.patchBatchEntriesError('orders:errors:patchBatchEntry:zeroOptionalArgs'),
      );
      return;
    }

    const patchResult: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.patchBatchEntries,
      batchEntriesIdsToUpdate,
      action.payload.parcel,
      action.payload.extras,
    );

    requestUpdatedTrack(action.payload.trackingReference, {
      url: patchResult.config.url || '',
      isError: false,
      status: patchResult.status,
      message: undefined,
    });

    if (action.payload.extras) {
      yield put(
        sendTrackEvent(ADDITIONAL_SERVICES_SAVE, {
          batchEntries: batchEntriesIdsToUpdate,
          payload: action.payload.extras,
        }),
      );
    }

    const batchEntries = patchResult.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.patchBatchEntriesSuccess(batchEntries));

    const batchEntryOrderIds = batchEntries.map((batchEntry) => batchEntry.orderObjectId);
    const ordersMap: HashMap<string, Order> =
      yield selectState<HashMap<string, Order>>(getOrdersData);

    const ordersWithInvalidOrProcessingBatchEntryStatus = batchEntries
      .filter((batchEntry: BatchEntryExtended) =>
        [
          ordersBatch.BatchEntryStatus.INVALID_ORDER,
          ...(action.payload.parcel ? [ordersBatch.BatchEntryStatus.PROCESSING_SHIPMENT] : []),
        ].includes(batchEntry.objectStatus),
      )
      .map((batchEntry) => batchEntry.orderObjectId);

    const ordersWithoutSenderAddress = batchEntryOrderIds.filter(
      (orderId) => !ordersMap[orderId].addressFrom,
    );

    const ordersToFetch = uniq(
      ordersWithInvalidOrProcessingBatchEntryStatus.concat(ordersWithoutSenderAddress),
    );
    // Update orders in the store that don't have sender addresses or orders that have been marked as invalid
    for (const orderId of ordersToFetch) {
      yield put(ordersActions.fetchOrder(orderId));
    }

    let responseOrderObjectIds: string[];

    if (action.payload.parcel) {
      // Ensure order has a toAddress, whether it's set in the order or in one of its batch entries
      const orderHasAddressToMap = batchEntries.reduce<Record<string, boolean>>(
        (map, batchEntry) => {
          map[batchEntry.orderObjectId] = !!batchEntry.addressTo;
          return map;
        },
        {},
      );

      responseOrderObjectIds = batchEntryOrderIds.filter(
        (orderId) => !!ordersMap[orderId].toAddress || orderHasAddressToMap[orderId],
      );
    } else {
      // Poll only those orders which have a package
      responseOrderObjectIds = batchEntryOrderIds.filter((orderId) => !!ordersMap[orderId].parcel);
    }

    yield put(ordersActions.startBatchEntriesPolling(responseOrderObjectIds));
  } catch (err) {
    if (axios.isAxiosError(err)) {
      requestUpdatedTrack(action.payload.trackingReference, {
        url: err.config.url || '',
        isError: true,
        status: err.response?.status || -1,
        message: err.message,
      });
    }

    yield put(ordersActions.patchBatchEntriesError(err));

    if (action.payload.extras) {
      yield put(sendTrackEvent(ADDITIONAL_SERVICES_SAVE_FAILED, { err }));
    }
  }
}

export function* updateParcel(action: ReturnType<typeof ordersActions.updateOrderParcel>) {
  try {
    yield put(startSubmit(PARCEL_FORM_NAME));
    const jsonParcel: parcel.SavedJSONParcel = parcel.toJSON(action.payload.parcel);

    const { batchEntriesIdsToUpdate } = yield call(
      getBatchEntriesIdsToUpdate,
      action.payload.orderSelection,
    );

    const updatedBatchEntriesResult: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.updateParcel,
      batchEntriesIdsToUpdate,
      jsonParcel,
    );

    requestUpdatedTrack(action.payload.trackingReference, {
      url: updatedBatchEntriesResult.config.url || '',
      isError: false,
      status: updatedBatchEntriesResult.status,
      message: undefined,
    });

    const batchEntries = updatedBatchEntriesResult.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.updateOrderParcelSuccess(batchEntries));

    const batchEntryOrderIds = batchEntries.map((batchEntry) => batchEntry.orderObjectId);
    const ordersMap: HashMap<string, Order> = yield select(getOrdersData);

    const ordersWithInvalidOrProcessingBatchEntryStatus = batchEntries
      .filter((batchEntry: BatchEntryExtended) =>
        [
          ordersBatch.BatchEntryStatus.INVALID_ORDER,
          ordersBatch.BatchEntryStatus.PROCESSING_SHIPMENT,
        ].includes(batchEntry.objectStatus),
      )
      .map((batchEntry) => batchEntry.orderObjectId);
    const ordersWithoutSenderAddress = batchEntryOrderIds.filter(
      (orderId) => !ordersMap[orderId].addressFrom,
    );

    const ordersToFetch = uniq(
      ordersWithInvalidOrProcessingBatchEntryStatus.concat(ordersWithoutSenderAddress),
    );
    // Update orders in the store that don't have sender addresses or orders that have been marked as invalid
    for (const orderId of ordersToFetch) {
      yield put(ordersActions.fetchOrder(orderId));
    }

    // Ensure order has a toAddress, whether it's set in the order or in one of its batch entries
    const orderHasAddressToMap = batchEntries.reduce<Record<string, boolean>>((map, batchEntry) => {
      map[batchEntry.orderObjectId] = !!batchEntry.addressTo;
      return map;
    }, {});

    const responseOrderObjectIds = batchEntryOrderIds.filter(
      (orderId) => !!ordersMap[orderId].toAddress || orderHasAddressToMap[orderId],
    );

    yield put(ordersActions.startBatchEntriesPolling(responseOrderObjectIds));

    // Using setSubmitFailed because that actually clears the value
    // of submitting, setSubmitSuccess does not.
    yield put(setSubmitFailed(PARCEL_FORM_NAME));
  } catch (err) {
    Sentry.captureException(err);
    if (axios.isAxiosError(err)) {
      requestUpdatedTrack(action.payload.trackingReference, {
        url: err.config.url || '',
        isError: true,
        status: err.response?.status || -1,
        message: err.message,
      });
    }

    yield put(ordersActions.updateOrderParcelError(err));

    const batchEntries: BatchEntryExtended[] = yield select(getBatchEntriesByOrderObjectIds, {
      orderObjectIds: Object.keys(action.payload.orderSelection),
    });

    const shipmentIds = batchEntries
      .map((batchEntry) => batchEntry.shipmentObjectId)
      .filter((shipmentId): shipmentId is string => shipmentId !== null);

    yield put(clearRates(shipmentIds));
  }
}

export function* updateBatchEntriesExtras(
  action: ReturnType<typeof ordersActions.updateBatchEntriesExtras>,
) {
  try {
    yield put(startSubmit(action.payload.form));

    const { batchEntriesIdsToUpdate } = yield call(
      getBatchEntriesIdsToUpdate,
      action.payload.orderSelection,
    );

    const result: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.updateBatchEntryExtras,
      batchEntriesIdsToUpdate,
      action.payload.extras,
    );

    yield put(
      sendTrackEvent(ADDITIONAL_SERVICES_SAVE, {
        batchEntries: batchEntriesIdsToUpdate,
        payload: action.payload.extras,
      }),
    );

    // Using setSubmitFailed because that actually clears the value
    // of submitting, setSubmitSuccess does not.
    yield put(setSubmitFailed(action.payload.form));

    const batchEntries = result.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.updateBatchEntriesExtrasSuccess(batchEntries));

    const batchEntryOrderIds = batchEntries.map((batchEntry) => batchEntry.orderObjectId);

    const ordersMap: HashMap<string, Order> = yield select(getOrdersData);

    const batchEntriesWithInvalidOrder = batchEntries
      .filter(
        (batchEntry: BatchEntryExtended) =>
          batchEntry.objectStatus === ordersBatch.BatchEntryStatus.INVALID_ORDER,
      )
      .map((batchEntry) => batchEntry.orderObjectId);
    const ordersWithoutSenderAddress = batchEntryOrderIds.filter(
      (orderId) => !ordersMap[orderId].addressFrom,
    );

    const ordersToFetch = uniq(batchEntriesWithInvalidOrder.concat(ordersWithoutSenderAddress));
    // Update orders in the store that don't have sender addresses or orders that have been marked as invalid
    for (const orderId of ordersToFetch) {
      yield put(ordersActions.fetchOrder(orderId));
    }

    // Poll only those orders which have a package
    const responseOrderObjectIds = batchEntryOrderIds.filter(
      (orderId) => !!ordersMap[orderId].parcel,
    );
    yield put(ordersActions.startBatchEntriesPolling(responseOrderObjectIds));
  } catch (err) {
    yield put(setSubmitFailed(action.payload.form));
    yield put(sendTrackEvent(ADDITIONAL_SERVICES_SAVE_FAILED, { err }));
  }
}

export function* patchUpdateBatchEntriesExtras(
  action: ReturnType<typeof ordersActions.patchUpdateBatchEntriesExtras>,
) {
  try {
    const { batchEntriesIdsToUpdate } = yield call(
      getBatchEntriesIdsToUpdate,
      action.payload.orderSelection,
    );

    const result: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.patchUpdateBatchEntryExtras,
      batchEntriesIdsToUpdate,
      action.payload.extras,
    );

    yield put(
      sendTrackEvent(ADDITIONAL_SERVICES_SAVE, {
        batchEntries: batchEntriesIdsToUpdate,
        payload: action.payload.extras,
      }),
    );

    const batchEntries = result.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.patchUpdateBatchEntriesExtrasSuccess(batchEntries));

    const batchEntryOrderIds = batchEntries.map((batchEntry) => batchEntry.orderObjectId);

    const ordersMap: HashMap<string, Order> = yield select(getOrdersData);

    const batchEntriesWithInvalidOrder = batchEntries
      .filter(
        (batchEntry: BatchEntryExtended) =>
          batchEntry.objectStatus === ordersBatch.BatchEntryStatus.INVALID_ORDER,
      )
      .map((batchEntry) => batchEntry.orderObjectId);

    const ordersWithoutSenderAddress = batchEntryOrderIds.filter(
      (orderId) => !ordersMap[orderId].addressFrom,
    );

    const ordersToFetch = uniq(batchEntriesWithInvalidOrder.concat(ordersWithoutSenderAddress));
    // Update orders in the store that don't have sender addresses or orders that have been marked as invalid
    for (const orderId of ordersToFetch) {
      yield put(ordersActions.fetchOrder(orderId));
    }

    // Poll only those orders which have a package
    const responseOrderObjectIds = batchEntryOrderIds.filter(
      (orderId) => !!ordersMap[orderId].parcel,
    );
    yield put(ordersActions.startBatchEntriesPolling(responseOrderObjectIds));
    // In case we want to run any function from the action dispatcher
    if (action.payload.callback) {
      action.payload.callback();
    }
  } catch (err) {
    yield put(ordersActions.patchUpdateBatchEntriesExtrasError(unknownToString(err)));
    yield put(sendTrackEvent(ADDITIONAL_SERVICES_SAVE_FAILED, { err }));
  }
}

export function* patchUpdateBatchEntriesExtrasPackingSlipNote(
  action: ReturnType<typeof ordersActions.patchUpdateBatchEntriesExtrasPackingSlipNote>,
) {
  try {
    let batchEntryIds: number[] = action.payload.batchEntryIds || [];
    //Create batch entries if the orders does not have batch entries (no batch entry ids passed)
    if (!action.payload.batchEntryIds) {
      const { batchEntriesIdsToUpdate } = yield call(
        getBatchEntriesIdsToUpdate,
        action.payload.orderSelection,
      );
      batchEntryIds = batchEntriesIdsToUpdate;
    }
    const extras: Partial<ShipmentExtras> = {
      packingSlipText: action.payload.packingSlipText,
    };
    const result: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.patchUpdateBatchEntryExtras,
      batchEntryIds,
      extras,
    );

    yield put(
      sendTrackEvent(ADDITIONAL_SERVICES_SAVE, {
        batchEntries: batchEntryIds,
        payload: extras,
      }),
    );

    const batchEntries = result.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.patchUpdateBatchEntriesExtrasSuccess(batchEntries));

    const batchEntryOrderIds = batchEntries.map((batchEntry) => batchEntry.orderObjectId);

    const ordersMap: HashMap<string, Order> = yield select(getOrdersData);

    const batchEntriesWithInvalidOrder = batchEntries
      .filter(
        (batchEntry: BatchEntryExtended) =>
          batchEntry.objectStatus === ordersBatch.BatchEntryStatus.INVALID_ORDER,
      )
      .map((batchEntry) => batchEntry.orderObjectId);

    const ordersWithoutSenderAddress = batchEntryOrderIds.filter(
      (orderId) => !ordersMap[orderId].addressFrom,
    );

    const ordersToFetch = uniq(batchEntriesWithInvalidOrder.concat(ordersWithoutSenderAddress));
    // Update orders in the store that don't have sender addresses or orders that have been marked as invalid
    for (const orderId of ordersToFetch) {
      yield put(ordersActions.fetchOrder(orderId));
    }

    // Poll only those orders which have a package
    const responseOrderObjectIds = batchEntryOrderIds.filter(
      (orderId) => !!ordersMap[orderId].parcel,
    );
    yield put(ordersActions.startBatchEntriesPolling(responseOrderObjectIds));

    yield put(
      addSnackbarMessage({
        message: 'orders:drawers:packingSlip:update:success',
        i18n: true,
        severity: 'success',
      }),
    );
  } catch (err) {
    yield put(ordersActions.patchUpdateBatchEntriesExtrasError(unknownToString(err)));
    yield put(sendTrackEvent(ADDITIONAL_SERVICES_SAVE_FAILED, { err }));
    yield put(
      addSnackbarMessage({
        message: 'orders:drawers:packingSlip:update:error',
        i18n: true,
        severity: 'error',
      }),
    );
  }
}

export function* updateBatchEntriesAddress({
  payload: {
    orderSelection,
    address: originalAddress,
    addressType,
    combineSenderReturnAddresses,
    createUserAddress,
    source,
    trackingReference,
  },
}: ReturnType<typeof ordersActions.updateBatchEntriesAddress>) {
  const batchEntryCount = Object.values(orderSelection).reduce((count, batchEntryIds) => {
    return count + (batchEntryIds.length || 1);
  }, 0);
  try {
    const { batchEntriesIdsToUpdate } = yield call(getBatchEntriesIdsToUpdate, orderSelection);

    const address = { ...originalAddress };

    if (
      [SENDER_ADDRESS_KIND, RETURN_ADDRESS_KIND].includes(addressType) &&
      !combineSenderReturnAddresses &&
      createUserAddress &&
      !!(address as APIAddress).id
    ) {
      // When updating a sender/return address AND NOT combining sender return addresses
      // Remove id from address object passed to editAPIAddress service to prevent both addresses
      // from being edited if the edge case where an order is currently using combined sender and return address
      delete (address as APIAddress).id;
    }

    const result: Awaited<ReturnType<typeof ordersService.updateBatchEntryAddress>> = yield call(
      ordersService.updateBatchEntryAddress,
      sanitizeAddress(address),
      addressType,
      uniq(batchEntriesIdsToUpdate),
    );

    if (typeof result?.data?.map !== 'function') {
      requestUpdatedTrack(trackingReference, {
        url: result.config.url || '',
        isError: true,
        status: result.status,
        message: 'updateBatchEntryAddress got unexpected result',
      });
      throw new Error(`updateBatchEntryAddress got unexpected result: ${result.data}`);
    }

    requestUpdatedTrack(trackingReference, {
      url: result.config.url || '',
      isError: false,
      status: result.status,
      message: undefined,
    });

    if (createUserAddress) {
      // Call this saga instead of dispatching the action to block subsequent yields
      // until the work in this saga is done
      // This prevents a race condition where the address book does not have the most up to date address
      // and a consumer attempts to compare selected address to the ones on address book
      yield call(
        addressSagas.createAPIAddress,
        addressActions.createAPIAddress({
          address,
          trackingReference,
        }),
      );
    }

    const parsedBatchEntries = result.data
      .map(batchEntryExtendedFromJSON)
      .filter((batchEntry) => !batchEntry.transactionObjectId);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));

    // Start polling the batch entries
    yield put(
      ordersActions.startBatchEntriesPolling(
        parsedBatchEntries.map((batchEntry) => batchEntry.orderObjectId),
      ),
    );

    yield put(
      ordersActions.updateBatchEntriesAddressSuccess(
        batchEntryCount,
        `orders:success:batch:${addressType}`,
        parsedBatchEntries,
        source,
      ),
    );
  } catch (err) {
    if (axios.isAxiosError(err)) {
      requestUpdatedTrack(trackingReference, {
        url: err.config.url || '',
        isError: true,
        status: err.response?.status || -1,
        message: err.message,
      });
    }

    yield put(
      ordersActions.updateBatchEntriesAddressError(
        batchEntryCount,
        `orders:errors:batch:${addressType}`,
        source,
      ),
    );
  }
}

export function* updateBatchEntriesServiceLevelToken(
  action: ReturnType<typeof ordersActions.updateBatchEntriesServiceLevelToken>,
) {
  try {
    const { orderSelection, preferredServiceLevelToken, accountObjectId } = action.payload;

    // don't call getBatchEntriesIdsToUpdate here.  That POSTs to the backend to get existing or
    // create new batch entries for the order as appropriate; but it also resets the batch entries
    // to PENDING status and clears their rates, which we don't want to happen when we're just
    // updating the service level token.
    //
    // This may need to be updated when we start allowing users to select service levels before
    // rates are available, if there are then cases where a user can select a service level for
    // an order that doesn't have a batch entry at all yet.
    const batchEntryIdsToUpdate = compact(flatten(Object.values(orderSelection))).map((idString) =>
      Number(idString),
    );

    const result: JSONBatchEntriesResponse = yield call(
      ordersService.updateBatchEntryServiceLevelToken,
      preferredServiceLevelToken,
      batchEntryIdsToUpdate,
      accountObjectId,
    );

    const parsedBatchEntries = result.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.updateBatchEntriesServiceLevelTokenSuccess(parsedBatchEntries));
  } catch (err) {
    yield put(ordersActions.updateBatchEntriesServiceLevelTokenError());
  }
}

export function* updateOrderWithBatchEntry(
  action: ReturnType<typeof ordersActions.updateOrderWithBatchEntry>,
) {
  const { serviceLevelToken, objectID, accountObjectId, closePopover } = action.payload;
  try {
    const results: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.getOrCloneBatchEntriesForOrders,
      [objectID],
      [],
    );

    if (closePopover) {
      closePopover();
    }
    const parsedBatchEntries = results.data.map(batchEntryExtendedFromJSON);
    if (parsedBatchEntries && parsedBatchEntries?.length !== 0) {
      yield put(ordersActions.updateOrderWithBatchEntrySuccess(parsedBatchEntries));
      const orderSelection: order.OrderSelection = {
        [parsedBatchEntries[0]?.orderObjectId]: [results.data[0]?.id],
      };
      const trackingReference = trackingHelper.getTrackingReference(
        RATES_UPDATED_EVENT.name,
        'other',
        {
          rateIds: [objectID],
        },
      );
      yield put(
        selectRate(
          objectID,
          '',
          true,
          trackingReference,
          orderSelection,
          serviceLevelToken,
          accountObjectId,
        ),
      );
    }
  } catch (err) {
    yield put(ordersActions.updateBatchEntriesServiceLevelTokenError());
  }
}

export function* updateBatchEntriesShipmentDate(
  action: ReturnType<typeof ordersActions.updateBatchEntriesShipmentDate>,
) {
  let orderObjectIds;

  try {
    const { orderSelection, shipmentDate } = action.payload;
    orderObjectIds = Object.keys(orderSelection);

    const { batchEntriesIdsToUpdate } = yield call(getBatchEntriesIdsToUpdate, orderSelection);

    // optimistically update the shipment date (so that the new date displays in the actions bar)
    yield put(
      ordersActions.updateBatchEntriesShipmentDateInitiated(batchEntriesIdsToUpdate, shipmentDate),
    );

    const result: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.updateBatchEntryShipmentDate,
      shipmentDate,
      uniq(batchEntriesIdsToUpdate),
    );

    const parsedBatchEntries = result.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));
    yield put(ordersActions.updateBatchEntriesShipmentDateSuccess());
  } catch (err) {
    yield put(ordersActions.updateBatchEntriesShipmentDateError());
  } finally {
    // Start polling the batch entries regardless of whether the update succeeded or failed (in the latter case,
    // we need to revert the optimistically-updated shipment date)
    if (orderObjectIds) {
      yield put(ordersActions.startBatchEntriesPolling(orderObjectIds));
    }
  }
}

export function* fetchOrderBatchEntry(
  action: ReturnType<typeof ordersActions.fetchOrderBatchEntry>,
) {
  try {
    const response: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.getOrderBatchEntry,
      action.payload.orderObjectId,
    );
    const batchEntriesExtended = response.data.map(batchEntryExtendedFromJSON);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(batchEntriesExtended));
  } catch (err) {
    yield put(ordersActions.fetchOrderBatchEntryError(action.payload.orderObjectId));
  }
}

// Batch entries polling logic
// Worker will be stored here to prevent starting many
// and also being able to kill it once it completed
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let worker: ForkEffect<SagaReturnType<any>> | null = null;
// Keep track of the order and the unix time when the poll started for it.
// So far that unix timestamp has no use, if we implement timeout messaging per
// order it will come in handy.
let ordersToPoll: { [key: string]: number } = {};
// Keep track of the shipment and the unix time when the poll started for it.
// So far that unix timestamp has no use, if we implement timeout messaging per
// order it will come in handy.
const shipmentsToPoll: { [key: string]: number } = {};

/**
 * This is a helper generator function to get all the batch entries for the orders
 * This would create new batch entry if it's missing for an order
 * @param orderObjectIDs
 * @returns
 */
export function* getBatchEntries(orderObjectIDs: string[]) {
  let batchEntries: BatchEntryExtended[] = [];
  let missingOrders: string[] = [];
  // Keep fetching batch entries until we get batch entry for
  // all the orders we are polling
  // We create a batch entry for each order
  // Issue resolved: https://shippo.atlassian.net/browse/ENG-41017
  try {
    const response: AxiosResponse<{ results: JSONBatchEntryExtended[] }> = yield call(
      ordersService.getOrderBatchEntries,
      missingOrders.length ? missingOrders : orderObjectIDs,
    );
    batchEntries = [...response.data.results.map(batchEntryExtendedFromJSON)];
    const orderBatchEntriesMap = batchEntries.reduce<{
      [orderObjectId: string]: BatchEntryExtended[];
    }>((map, be) => {
      if (map[be.orderObjectId]) {
        map[be.orderObjectId].push(be);
      } else {
        map[be.orderObjectId] = [be];
      }
      return map;
    }, {});

    missingOrders = orderObjectIDs.filter((objectId) => !orderBatchEntriesMap[objectId]);
    // We create batch entries for the orders with missing batch entry
    if (missingOrders.length > 0) {
      const response: AxiosResponse<{ results: JSONBatchEntryExtended[] }> = yield call(
        ordersService.getOrCloneBatchEntriesForOrders,
        missingOrders,
        [],
      );
      batchEntries = [...batchEntries, ...response.data.results.map(batchEntryExtendedFromJSON)];
    }
  } catch (err) {
    yield put(
      ordersActions.fetchOrderBatchEntriesError(missingOrders ? missingOrders : orderObjectIDs),
    );
  }

  return batchEntries;
}

export function* batchEntriesWorker(
  ordersToPoll: { [key: string]: number },
  shipmentsToPoll: { [key: string]: number },
) {
  while (true) {
    try {
      const orderObjectIds = Object.keys(ordersToPoll);

      if (orderObjectIds.length > 0) {
        yield put(ordersActions.fetchOrderBatchEntries(orderObjectIds));
        const batchEntries = yield call(getBatchEntries, orderObjectIds);
        const completedBatchEntries: BatchEntryExtended[] = [];
        const ordersData: { [orderObjectId: string]: Order } = yield select(getOrdersData);
        const ordersBatchEntryIsTerminalPollingState = Object.values(ordersData).reduce(
          (
            map: {
              [orderObjectId: string]: {
                toPoll: number[];
                donePolling: number[];
              };
            },
            order,
          ) => {
            if (order.partialFulfillmentRequests.length) {
              const emptyArray: number[] = [];
              const entry = {
                toPoll: order.partialFulfillmentRequests.map((be) => be.id),
                donePolling: emptyArray,
              };
              return {
                ...map,
                [order.objectId]: entry,
              };
            }
            return map;
          },
          {},
        );
        for (const batchEntry of batchEntries) {
          let batchEntryInTerminalPollingState = false;
          if (
            [
              ordersBatch.BatchEntryStatus.SHIPMENT_REQUEST_FAILED,
              ordersBatch.BatchEntryStatus.TRANSACTION_REQUEST_FAILED,
              ordersBatch.BatchEntryStatus.INVALID_ORDER,
              ordersBatch.BatchEntryStatus.PENDING,
              ordersBatch.BatchEntryStatus.COMPLETE_TRANSACTION,
            ].includes(batchEntry.objectStatus)
          ) {
            completedBatchEntries.push(batchEntry);
            batchEntryInTerminalPollingState = true;
          }

          if (
            batchEntry.shipmentObjectId &&
            batchEntry.objectStatus === ordersBatch.BatchEntryStatus.COMPLETE_SHIPMENT
          ) {
            shipmentsToPoll[batchEntry.shipmentObjectId] = new Date().getTime();
            completedBatchEntries.push(batchEntry);
            batchEntryInTerminalPollingState = true;
          }

          // until we refactor to support poll by batch entry id, we need to prevent order from being excluded before all
          // batch entries to that order are in a terminal state
          // this currently causes some fresh rates to go into loading state intermittently until all batch entries
          // reach a terminal polling state
          if (batchEntryInTerminalPollingState) {
            // orders with multiple outbound batch entries and some may not be in terminal state. we need to continue polling
            if (
              batchEntry.partialFulfillmentRank > 0 &&
              ordersBatchEntryIsTerminalPollingState[batchEntry.orderObjectId]
            ) {
              ordersBatchEntryIsTerminalPollingState[batchEntry.orderObjectId].donePolling = [
                ...ordersBatchEntryIsTerminalPollingState[batchEntry.orderObjectId].donePolling,
                batchEntry.id,
              ];
              if (
                ordersBatchEntryIsTerminalPollingState[batchEntry.orderObjectId].donePolling
                  .length ===
                ordersBatchEntryIsTerminalPollingState[batchEntry.orderObjectId].toPoll.length
              ) {
                delete ordersToPoll[batchEntry.orderObjectId];
              }
            }
            // order with only a single outbound batch entry. we are done polling
            else {
              delete ordersToPoll[batchEntry.orderObjectId];
            }
          }
        }

        yield put(ordersActions.fetchOrderBatchEntriesSuccess(completedBatchEntries));
      }

      const shipmentObjectIds = Object.keys(shipmentsToPoll);

      if (shipmentObjectIds.length > 0) {
        const shipmentsData: { [shipmentId: string]: Maybe<Shipment> } =
          yield select(getShipmentsData);

        for (const shipmentObjectId of shipmentObjectIds) {
          const trackingReference = trackingHelper.getTrackingReference(
            RATES_UPDATED_EVENT.name,
            'other',
            {
              rateIds: undefined,
            },
          );
          const shipmentIsLoading: boolean = yield select(getShipmentIsLoading, {
            shipmentId: shipmentObjectId,
          });

          if (shipmentIsLoading) continue;

          const shipment = shipmentsData[shipmentObjectId];
          if (!shipment) {
            yield put(batchAction(fetchShipment(shipmentObjectId, trackingReference)));
            shipmentsToPoll[shipmentObjectId] = new Date().getTime();
            continue;
          }

          if ([ShipmentStatus.SUCCESS, ShipmentStatus.ERROR].includes(shipment.status)) {
            delete shipmentsToPoll[shipment.objectId];
          } else {
            yield put(fetchShipment(shipmentObjectId, trackingReference));
          }
        }
      }

      if (Object.keys(ordersToPoll).length === 0 && Object.keys(shipmentsToPoll).length === 0) {
        yield put(ordersActions.stopBatchEntriesPolling());
      } else {
        // Prevent timeout
        if (ENV_ID === 'test') {
          break;
        }
        yield delay(750);
      }
    } catch (err) {
      Sentry.captureException(`Error in batchEntriesWorker ${err}`, {
        level: 'error' as Sentry.SeverityLevel,
      });
      // TODO: Set errors for batch entries that we were still polling for
      yield put(ordersActions.stopBatchEntriesPolling());
    }

    if (ENV_ID === 'test') {
      break;
    }
  }
}

function* batchEntriesPollRace() {
  // TODO: What is a better type for this?
  const result: { timeout: boolean } = yield race({
    task: call(batchEntriesWorker, ordersToPoll, shipmentsToPoll),
    cancel: take([ActionKeys.ORDERS_BATCH_ENTRIES_POLL_END]),
    timeout: delay(1 * 60 * 1000, true),
  });

  if (result.timeout) {
    ordersToPoll = {};
    // Dispatch timeout action?
  }

  worker = null;
}

export function* batchEntriesPollWatcher() {
  while (true) {
    const action:
      | ReturnType<typeof ordersActions.startBatchEntriesPolling>
      | ReturnType<typeof ordersActions.fetchOrdersSuccess> = yield take([
      ActionKeys.ORDERS_BATCH_ENTRIES_POLL_START,
      ActionKeys.ORDERS_FETCH_SUCCESS,
    ]);

    const isOldExpChosen: boolean = yield selectState<boolean>(getIsOldExpChosen);

    if (isOldExpChosen) continue;

    let orderObjectIds: string[] = [];

    switch (action.type) {
      case ActionKeys.ORDERS_BATCH_ENTRIES_POLL_START:
        orderObjectIds = action.payload.orderObjectIds;
        break;

      case ActionKeys.ORDERS_FETCH_SUCCESS:
        orderObjectIds = action.payload.orders.map((order) => order.objectId);
        break;
    }

    for (const orderObjectId of orderObjectIds) {
      ordersToPoll[orderObjectId] = new Date().getTime();
    }

    if (!worker) {
      worker = yield fork(batchEntriesPollRace);
    }
  }
}

export function* batchEntriesRegen(action: ReturnType<typeof ordersActions.createAnotherLabel>) {
  try {
    const orderSelection = action.payload.orderSelection;
    const orderObjectIDs: string[] = [];
    const batchEntryIDs: ordersBatch.BatchEntryId[] = [];
    const initialBatchEntries: BatchEntryExtended[] = yield call(
      orderSelectionToBatchEntries,
      orderSelection,
    );

    Object.entries(orderSelection).forEach(([orderObjectId, orderBatchEntryIDs]) => {
      orderObjectIDs.push(orderObjectId);
      if (orderBatchEntryIDs?.length > 0) {
        batchEntryIDs.push(...orderBatchEntryIDs);
      }
    });

    const results: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.getOrCloneBatchEntriesForOrders,
      orderObjectIDs,
      batchEntryIDs,
    );

    const parsedBatchEntries = results.data.map(batchEntryExtendedFromJSON);

    // make sure to reset split orders that were not included in the initial request but returned in the response
    const resetBatchEntries = findResetBatchEntries(initialBatchEntries, parsedBatchEntries);

    yield put(ordersActions.clearBatchEntries(initialBatchEntries, resetBatchEntries));

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));
    yield put(
      ordersActions.startBatchEntriesPolling(
        resetBatchEntries.map((batchEntry) => batchEntry.orderObjectId),
      ),
    );

    if (parsedBatchEntries.length === 1) {
      const location: LocationDescriptorObject =
        yield selectState<LocationDescriptorObject>(getLocation);
      yield put(
        push(
          `/orders/${parsedBatchEntries[0].orderObjectId}/shipments/${parsedBatchEntries[0].id}${location.search}`,
        ),
      );
    }
  } catch (err) {
    // todo: raise error in log
  }
}

export function* retryShipment(action: ReturnType<typeof ordersActions.retryShipment>) {
  try {
    const orderSelection = action.payload.orderSelection;
    const batchEntryIDs: ordersBatch.BatchEntryId[] = [];

    Object.entries(orderSelection).forEach(([, orderBatchEntryIDs]) => {
      if (orderBatchEntryIDs?.length > 0) {
        batchEntryIDs.push(...orderBatchEntryIDs);
      }
    });

    const results: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.refreshBatchEntry,
      batchEntryIDs,
    );

    const parsedBatchEntries = results.data.map(batchEntryExtendedFromJSON);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));
    yield put(
      ordersActions.startBatchEntriesPolling(
        parsedBatchEntries.map((batchEntry) => batchEntry.orderObjectId),
      ),
    );
    if (parsedBatchEntries.length === 1 && batchEntryIDs[0] !== parsedBatchEntries[0].id) {
      const location: LocationDescriptorObject =
        yield selectState<LocationDescriptorObject>(getLocation);
      yield put(
        push(
          `/orders/${parsedBatchEntries[0].orderObjectId}/shipments/${parsedBatchEntries[0].id}${location.search}`,
        ),
      );
    }
  } catch (err) {
    // todo: raise error in log
  }
}

// fetches applied rules for a batch entry given batch entry id
export function* fetchBatchEntryRules(
  action: ReturnType<typeof ordersActions.fetchBatchEntryRules>,
) {
  try {
    const response: AxiosResponse<{ rule: JSONBusinessRule }[]> = yield call(
      ordersService.fetchBatchEntryRules,
      action.payload.batchEntryId,
    );
    if (response.data.length === 0) {
      yield put(ordersActions.clearBatchEntryRules(action.payload.batchEntryId));
      return;
    }

    // for the /batch_entry/<id>/rule GET, returns an array of batch entry rule responses with rule nested
    const rule = parseBusinessRules(response.data.map((batchEntryRule) => batchEntryRule?.rule));

    yield put(
      ordersActions.fetchBatchEntryRulesSuccess(action.payload.batchEntryId, rule.businessRules),
    );
  } catch (err) {
    yield put(ordersActions.fetchBatchEntryRulesError(unknownToString(err)));
  }
}

export function* createPartialFulfillmentRequests(
  action: ReturnType<typeof ordersActions.createPartialFulfillmentRequests>,
) {
  try {
    yield put(startSubmit(SPLIT_ORDERS_FORM));
    const { orderObjectId, partialFulfillments } = action.payload;

    const filteredPartialFulfillments = partialFulfillments.filter((contents) => {
      return contents.some((item) => item.quantity > 0);
    });

    yield put(
      sendTrackEvent(SPLIT_ORDER_MODAL_SAVE, {
        numberOfShipments: filteredPartialFulfillments.length ?? 0,
      }),
    );

    const response: AxiosResponse<order.JSONOrder[]> = yield call(
      ordersService.createPartialFulfillmentRequests,
      orderObjectId,
      filteredPartialFulfillments.map((contents) =>
        contents.map(partialFulfillmentItemContentToJSON),
      ),
    );

    yield put(stopSubmit(SPLIT_ORDERS_FORM));
    yield put(setSubmitSucceeded(SPLIT_ORDERS_FORM));
    const orderWithPartialFulfillmentRequests = order.fromJSON(response.data[0]);
    yield put(
      ordersActions.createPartialFulfillmentRequestsSuccess(orderWithPartialFulfillmentRequests),
    );
    yield put(ordersActions.closeOrderSplitModal());

    const pathName: ReturnType<typeof getPathname> = yield select(getPathname);
    const batchEntryId = Number(pathName.slice(pathName.lastIndexOf('/') + 1, pathName.length));
    const batchEntry: ReturnType<typeof getBatchEntryById> = !isNaN(batchEntryId)
      ? yield select(getBatchEntryById, {
          batchEntryId,
        })
      : null;
    const isPreSplit = pathName === `/orders/${orderWithPartialFulfillmentRequests.objectId}`;
    const qs: ReturnType<typeof getQS> = yield select(getQS);

    if (batchEntry || isPreSplit) {
      // find current one by rank
      const currentWorkingBatchEntry =
        orderWithPartialFulfillmentRequests.partialFulfillmentRequests.find((be) => {
          if (!batchEntry) {
            return true;
          }
          const isSingleBatchEntryToSplit =
            batchEntry.partialFulfillmentRank === 0 && be.partialFulfillmentRank === 1;
          const isExistingSplit = be.partialFulfillmentRank === batchEntry.partialFulfillmentRank;
          const isDeletedSplit =
            batchEntry.partialFulfillmentRank >
            orderWithPartialFulfillmentRequests.partialFulfillmentRequests.length;

          if (isSingleBatchEntryToSplit || isExistingSplit || isDeletedSplit) {
            return true;
          }
        });

      if (currentWorkingBatchEntry) {
        yield put(
          replace(
            `/orders/${orderWithPartialFulfillmentRequests.objectId}/shipments/${currentWorkingBatchEntry.id}${qs}`,
          ),
        );
      } else {
        yield put(replace(`/orders/${orderWithPartialFulfillmentRequests.objectId}${qs}`));
      }
    }
  } catch (err) {
    const { orderObjectId } = action.payload;
    yield put(stopSubmit(SPLIT_ORDERS_FORM));
    yield put(setSubmitFailed(SPLIT_ORDERS_FORM));
    // setting a boolean for this for now but once we have non-generic
    // backend errors we want to surface to the client this should be updated
    yield put(ordersActions.createPartialFulfillmentRequestsError(orderObjectId, 'network error'));
  }
}

function* toggleCheckoutModal(orders: order.OrderSelection) {
  const preCheckoutQueue: PreCheckoutSteps[] = [];

  const hasValidBilling: boolean = yield selectState<boolean>(userHasValidBilling);
  const isBillingBlocked: boolean = yield selectState<boolean>(userIsBillingBlocked);
  const isHighFraudRisk: boolean = yield selectState<boolean>(userIsHighFraudRisk);
  const registrationStatuses: RegStatusByCarrierAccount = yield select(
    getIncompleteRegStatusFromRates,
    {
      batchEntryIds: flatten(Object.values(orders)),
    },
  );
  const carrierAccountsMap: { [objectId: string]: CarrierAccount } = yield select(
    getCarrierAccountsObjectIdMap,
  );
  const hasValidSenderAddress: boolean = yield selectState<boolean>(getIsUserAddressValid);
  const requiresEmailVerification: boolean = yield selectState<boolean>(
    getRequiresEmailVerification,
  );

  // Queue - order matters
  if (isBillingBlocked && isHighFraudRisk) {
    // Show generic contact customer support modal if user has blocked billing and is high risk
    preCheckoutQueue.push({ kind: 'account-alert' });
  }

  for (const [carrierAccountObjectId, regStatus] of Object.entries(registrationStatuses)) {
    const carrierAccount: CarrierAccount = carrierAccountsMap[carrierAccountObjectId];

    switch (carrierAccount.carrier.id) {
      case UPS.id:
        // Retrieve country from master account's metadata. Currently used to identify country of UPS master accounts.
        let accountCountry: CountryIso2 | undefined;
        if (
          carrierAccount.metadata &&
          countries[carrierAccount.metadata as keyof typeof countries]
        ) {
          accountCountry = countries[carrierAccount.metadata as keyof typeof countries].iso2;
        }
        preCheckoutQueue.push({
          kind: 'ups-activation',
          // Show full UPS form if the incomplete reg status is not PENDING_TERMS (e.g. ERROR)
          showFullForm: !(regStatus === RegStatus.PENDING_TERMS && hasValidSenderAddress),
          accountCountry: accountCountry,
        });
        break;
      case CANADA_POST.id:
        preCheckoutQueue.push({
          kind: 'canada_post-activation',
          showFullForm: true,
        });
        break;
      case DHL_EXPRESS.id:
        preCheckoutQueue.push({
          kind: 'dhl_express-activation',
        });
        break;
    }
  }

  if (!hasValidBilling) {
    if (requiresEmailVerification) {
      preCheckoutQueue.push({ kind: 'email-verification' });
    }

    preCheckoutQueue.push({ kind: 'credit-card-info' });
  }

  yield put(ordersActions.toggleCheckoutModal(orders, preCheckoutQueue));
}

export function* startCheckout(
  action: ReturnType<typeof ordersActions.startCheckout>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Generator<unknown, unknown, any> {
  try {
    const isSplitOrder: boolean = yield select(getIsAnyBatchEntryASplitOrder, {
      batchEntryIds: flatten(Object.values(action.payload.orders)),
    });

    yield put(
      sendTrackEvent(ORDER_CHECKOUT_STARTED, {
        'Is Split Order': isSplitOrder,
      }),
    );

    const isIDVRequired: boolean = yield selectState<boolean>(getIsIDVRequired);

    if (!isIDVRequired) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return yield call(toggleCheckoutModal, action.payload.orders) as any;
    }

    const result: AxiosResponse<IDVerification.JSONIDVerification> =
      yield call(getVerificationStatus);
    const verification = IDVerification.fromJSON(result.data);

    switch (verification.state) {
      // User was previously in verification but now it has succeeded.
      case IDVerification.IDVerificationStatus.NO_VERIFICATION_NEEDED:
      case IDVerification.IDVerificationStatus.VERIFICATION_SUCCEEDED:
        // Calling refresh user saga directly. This will wait until refresh is done
        // and avoid that the selectors following this will work with stale data.
        yield call(refreshUser);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return yield call(toggleCheckoutModal, action.payload.orders) as any;

      default:
        // Dispatch action to set verification step
        yield put(setIdVerificationStatus(verification));
    }
  } catch (err) {
    // This is likely to be an error retrieving verification status. Handle that with a generic message.
    yield put(
      setIdVerificationStatus({
        state: IDVerification.IDVerificationStatus.VERIFICATION_FAILED_NO_RETRY,
        errors: ['error_processing_request'],
      }),
    );
  }
}

export function* openOrderSplitModal(action: ReturnType<typeof ordersActions.openOrderSplitModal>) {
  const { orderObjectId } = action.payload;
  yield put(
    sendTrackEvent(SPLIT_ORDER_MODAL_OPENED, {
      orderObjectId,
    }),
  );
}

export function* closeOrderSplitModal() {
  yield put(sendTrackEvent(SPLIT_ORDER_MODAL_CLOSED));
}

export function* applyParcelTemplateToOrderParcelForm(
  action: ReturnType<typeof ordersActions.applyParcelTemplateToOrderParcelForm>,
) {
  try {
    const { parcelTemplate } = action.payload;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formValues: { extras: any; useImportedOrderWeight: any } = yield select(
      getFormValues(PARCEL_FORM_NAME),
    );
    // Apply parcel template onto order parcel form
    yield put(change(PARCEL_FORM_NAME, 'parcelTemplate', parcelTemplate));
    // Set is using custom dimenstions to false, so the parcel template selection is visible
    yield put(
      change(PARCEL_FORM_NAME, 'extras', {
        ...formValues.extras,
        isUsingCustomDimensions: false,
      }),
    );
    // Set the weight for the parcel template form
    if (
      !!parcelTemplate.weight &&
      parcelTemplate.weight !== 0 &&
      !formValues.useImportedOrderWeight
    ) {
      yield put(change(PARCEL_FORM_NAME, 'weight', parcelTemplate.weight));
    }
  } catch (e) {
    Sentry.captureException(e);
  }
}

export function* updateExpiredShipments() {
  try {
    const orderSelection: order.OrderSelection = yield select(
      getUnpurchasedBatchEntriesOrderSelectionWithExpiredShipmentDate,
    );

    if (Object.keys(orderSelection).length) {
      yield put(ordersActions.updateBatchEntriesShipmentDate(orderSelection, Date.now()));
    }
  } catch (e) {
    Sentry.captureException(e);
  }
}

export function* reStartCheckout() {
  try {
    // We are fetching all the transactions which were failed last time
    // so that we can re-run checkout on them once again.
    // The reason it's not passed as a prop because
    // the action dispatcher component should not be re-rendered based on the data
    const transactions: { [transactionId: string]: Readonly<Transaction> } = yield selectState<{
      [transactionId: string]: Readonly<Transaction>;
    }>(getTransactionsData);
    const selectedOrders: order.OrderSelection =
      yield selectState<order.OrderSelection>(getSelectedOrders);
    const transactionsForRerun = Object.values(transactions).filter(
      (transaction) =>
        transaction.status === TransactionStatus.ERROR &&
        transaction.order &&
        selectedOrders[transaction.order],
    );
    if (transactionsForRerun.length) {
      const orderSelection: order.OrderSelection = transactionsForRerun.reduce<{
        [key: string]: number[];
      }>((newOrders, transaction) => {
        if (transaction.order) {
          newOrders[transaction.order] = selectedOrders[transaction.order];
        }
        return newOrders;
      }, {});
      // This step is important. After updating the address it's possible
      // that the selected orders now have updated batch entry IDs
      // We need to derive new selected orders with new batch entry IDs
      const { newSelectedOrder } = yield call(getBatchEntriesIdsToUpdate, orderSelection);

      // We also need to update the order after bypassing an address,
      // otherwise order is still invalid, which keeps the
      // confirmation drawer in a loading state
      yield put(ordersActions.retryShipment(newSelectedOrder));

      // This is always called on side panel
      yield put(ordersActions.startCheckout(newSelectedOrder, true));
    }
  } catch (e) {
    Sentry.captureException(e);
  }
}

export default [
  ordersPollWatcher(),
  batchEntriesPollWatcher(),
  firstTimeSyncWatcher(),
  takeEvery(ActionKeys.ORDER_FETCH, fetchOrder),
  takeLatest(ActionKeys.ORDERS_FETCH, fetchOrders),
  takeLatest(ActionKeys.ORDERS_SYNC, syncOrders),
  takeLatest(ActionKeys.ORDERS_GET_ALL_LABELS, getAllLabels),
  takeLatest(ActionKeys.ORDERS_GET_LABEL, getLabel),
  takeLeading(ActionKeys.ORDERS_GET_PACKING_SLIP, getOrderPackingSlip),
  takeLatest(ActionKeys.ORDERS_SEND_EMAIL_NOTIFICATION, sendEmailNotification),
  takeLatest(ActionKeys.BATCH_CREATE, createBatch),
  takeEvery(ActionKeys.ORDER_HIDE, hideOrder),
  takeEvery(ActionKeys.ORDERS_UPDATE_PARCEL, updateParcel),
  takeEvery(ActionKeys.FETCH_ORDER_TRANSACTIONS, fetchOrderTransactions),
  takeEvery(ActionKeys.ORDER_BATCH_ENTRY_FETCH, fetchOrderBatchEntry),
  takeEvery(ActionKeys.ORDERS_COUNT_FETCH, countOrders),
  takeLatest(ActionKeys.ORDERS_PATCH_BATCH_ENTRIES, patchBatchEntries),
  takeLatest(ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_EXTRAS, updateBatchEntriesExtras),
  takeLatest(ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS, patchUpdateBatchEntriesExtras),
  takeLatest(
    ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_PACKING_SLIP_NOTE,
    patchUpdateBatchEntriesExtrasPackingSlipNote,
  ),
  takeEvery(ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_ADDRESS, updateBatchEntriesAddress),
  takeEvery(ActionKeys.ORDERS_CREATE_ANOTHER_LABEL, batchEntriesRegen),
  takeLatest(ActionKeys.ORDERS_BATCH_ENTRY_RETRY_SHIPMENT, retryShipment),
  takeLatest(ActionKeys.ORDERS_CHECKOUT_SEND_EMAIL_NOTIFICATIONS, checkoutSendEmailNotification),
  takeLatest(ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE, updateBatchEntriesShipmentDate),
  takeLatest(
    ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN,
    updateBatchEntriesServiceLevelToken,
  ),
  takeLatest(ActionKeys.ORDER_UPDATE_WITH_BATCH_ENTRY, updateOrderWithBatchEntry),
  takeLatest(ActionKeys.BATCH_ENTRY_RULES_FETCH, fetchBatchEntryRules),
  takeLatest(
    ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS,
    createPartialFulfillmentRequests,
  ),
  takeLatest(ActionKeys.ORDERS_START_CHECKOUT, startCheckout),
  takeLatest(ActionKeys.ORDERS_RESTART_CHECKOUT, reStartCheckout),
  takeLeading(ActionKeys.ORDER_SPLIT_MODAL_OPEN, openOrderSplitModal),
  takeLeading(ActionKeys.ORDER_SPLIT_MODAL_CLOSE, closeOrderSplitModal),
  takeLatest(
    ActionKeys.ORDER_APPLY_PARCEL_TEMPLATE_TO_PARCEL_FORM,
    applyParcelTemplateToOrderParcelForm,
  ),
  takeLatest(ActionKeys.ORDERS_FETCH_SUCCESS, fetchOrderTags),
  // Commenting out the following action dispatcher due to potential system outage from increased
  // put calls originating from targetted saga
  // takeLatest(ActionKeys.ORDERS_BATCH_ENTRIES_POLL_END, updateExpiredShipments),
];
